import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, formatApiError } from "@/lib/api";

export function StockManager({ headers }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    http.get("/admin/stock", { headers }).then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [headers]);

  const toggle = async (row, inStock) => {
    const k = `${row.product_id}|${row.shade}`;
    setBusy(k);
    try {
      const { data } = await http.put("/admin/stock", { product_id: row.product_id, shade: row.shade, sold_out: !inStock }, { headers });
      setRows((prev) => prev.map((r) => (r.product_id === row.product_id && r.shade === row.shade
        ? { ...r, sold_out: !inStock, waiting: inStock ? r.waiting - (data?.notified ?? 0) : r.waiting } : r)));
      if (inStock && data?.notified) toast.success(`${row.shade} back in stock — ${data.notified} shopper${data.notified > 1 ? "s" : ""} emailed`);
      else toast.success(`${row.shade} marked ${inStock ? "in stock" : "sold out"}`);
      if (data?.failed) toast.error(`${data.failed} restock email${data.failed > 1 ? "s" : ""} failed — they'll be retried next time`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="stock-manager">
      <p className="text-sm text-[#6E504A] mb-4">Switch a shade off when it sells out. Shoppers can then ask to be emailed, and they're all notified the moment you switch it back on.</p>
      <ul className="divide-y divide-[#E6D7CC]">
        {rows.map((r) => {
          const slug = `${r.product_id}-${r.shade.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <li key={slug} className="py-3 flex items-center justify-between gap-4 text-sm" data-testid={`stock-row-${slug}`}>
              <div className="min-w-0">
                <p className="font-display text-lg">{r.shade}</p>
                <p className="text-[#6E504A]">{r.name}</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                {r.waiting > 0 && <span className="flex items-center gap-1 text-xs text-[#B4566A]" data-testid={`stock-waiting-${slug}`}><Bell className="w-3.5 h-3.5" />{r.waiting} waiting</span>}
                <span className={`text-xs w-16 text-right ${r.sold_out ? "text-[#B4566A]" : "text-[#4A2E2B]"}`}>{r.sold_out ? "Sold out" : "In stock"}</span>
                <Switch checked={!r.sold_out} disabled={busy === `${r.product_id}|${r.shade}`} onCheckedChange={(v) => toggle(r, v)} data-testid={`stock-toggle-${slug}`} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
